/**
 * Media & Content Management Page
 * ================================
 * صفحة إدارة المحتوى والوسائط
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTenantAuth } from '../../context/TenantAuthContext';
import { useToast } from '../../context/ToastContext';
import '../shared/PageStyles.css';
import { useDialog } from '../../../context/DialogContext';

const I = {
  upload: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
  image: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  file: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="13 2 13 9 20 9"/></svg>,
  video: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>,
  link: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
};

const MediaPage = () => {
  const { apiCall } = useTenantAuth();
  const { success, error: showError } = useToast();
  const { confirm } = useDialog();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('gallery');
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const loadMedia = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiCall('/tenant/media');
      setMedia(data.items || []);
    } catch (err) {
      showError('فشل تحميل الوسائط');
    } finally {
      setLoading(false);
    }
  }, [apiCall, showError]);

  useEffect(() => { loadMedia(); }, [loadMedia]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      showError('حجم الملف كبير جداً (الحد الأقصى 10 ميجابايت)');
      return;
    }
    
    setSelectedFile(file);
    if (file.type.startsWith('image/')) {
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      setPreviewUrl(null);
    }
  };

  const uploadFile = async () => {
    if (!selectedFile) return;
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('type', selectedFile.type.startsWith('image/') ? 'image' : 'document');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8002/api/v1'}/tenant/media`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('watrly_tenant_token')}`
        },
        body: formData
      });
      
      if (!response.ok) throw new Error('فشل رفع الملف');
      
      success('تم رفع الملف بنجاح');
      setSelectedFile(null);
      setPreviewUrl(null);
      loadMedia();
    } catch (err) {
      showError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const deleteMedia = async (id) => {
    if (!await confirm({ title: 'حذف الملف', message: 'هل أنت متأكد من حذف هذا الملف؟', type: 'delete', confirmText: 'حذف', cancelText: 'تراجع' })) return;
    try {
      await apiCall(`/tenant/media/${id}`, { method: 'DELETE' });
      success('تم حذف الملف');
      loadMedia();
    } catch (err) {
      showError('فشل حذف الملف');
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    success('تم نسخ الرابط');
  };

  const getFileIcon = (type) => {
    if (type?.startsWith('image/')) return I.image;
    if (type?.startsWith('video/')) return I.video;
    return I.file;
  };

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header__title">
          <h1>إدارة المحتوى</h1>
          <p>{media.length} ملف</p>
        </div>
        <button className="btn btn--primary" onClick={() => fileInputRef.current?.click()}>
          {I.upload} <span>رفع ملف</span>
        </button>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept="image/*,video/*,.pdf,.doc,.docx"
          onChange={handleFileSelect}
        />
      </div>

      <div className="tabs">
        <button className={`tab ${activeTab === 'gallery' ? 'active' : ''}`} onClick={() => setActiveTab('gallery')}>المعرض</button>
        <button className={`tab ${activeTab === 'documents' ? 'active' : ''}`} onClick={() => setActiveTab('documents')}>المستندات</button>
      </div>

      {/* Upload Preview */}
      {selectedFile && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {previewUrl ? (
              <img src={previewUrl} alt="Preview" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px' }} />
            ) : (
              <div style={{ width: '80px', height: '80px', background: 'var(--tenant-bg)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {getFileIcon(selectedFile.type)}
              </div>
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '600' }}>{selectedFile.name}</div>
              <div style={{ fontSize: '13px', color: 'var(--tenant-text-muted)' }}>{formatSize(selectedFile.size)}</div>
            </div>
            <button className="btn btn--secondary" onClick={() => { setSelectedFile(null); setPreviewUrl(null); }}>{I.x}</button>
            <button className="btn btn--primary" onClick={uploadFile} disabled={uploading}>
              {uploading ? 'جاري الرفع...' : 'رفع'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="page-loading"><div className="spinner" /></div>
      ) : media.length === 0 ? (
        <div className="page-empty">{I.image}<h3>لا يوجد ملفات</h3><p>ابدأ برفع الصور والملفات</p></div>
      ) : activeTab === 'gallery' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '16px' }}>
          {media.filter(m => m.type === 'image').map(m => (
            <div key={m.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ position: 'relative', paddingTop: '100%', background: 'var(--tenant-bg)' }}>
                <img
                  src={m.url}
                  alt={m.filename}
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <div style={{ position: 'absolute', top: '8px', left: '8px', display: 'flex', gap: '4px' }}>
                  <button className="action-btn" style={{ background: 'white' }} onClick={() => copyUrl(m.url)}>{I.link}</button>
                  <button className="action-btn action-btn--danger" style={{ background: 'white' }} onClick={() => deleteMedia(m.id)}>{I.trash}</button>
                </div>
              </div>
              <div style={{ padding: '12px' }}>
                <div style={{ fontSize: '13px', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.filename}</div>
                <div style={{ fontSize: '11px', color: 'var(--tenant-text-muted)' }}>{formatSize(m.size)}</div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>الملف</th>
                <th>النوع</th>
                <th>الحجم</th>
                <th>التاريخ</th>
                <th>الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {media.filter(m => m.type !== 'image').map(m => (
                <tr key={m.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '36px', height: '36px', background: 'var(--tenant-bg)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--tenant-primary)' }}>
                        {getFileIcon(m.mime_type)}
                      </div>
                      <span style={{ fontWeight: '500' }}>{m.filename}</span>
                    </div>
                  </td>
                  <td>{m.mime_type || '-'}</td>
                  <td>{formatSize(m.size)}</td>
                  <td style={{ fontSize: '12px', color: 'var(--tenant-text-muted)' }}>{m.created_at ? new Date(m.created_at).toLocaleDateString('ar-SA') : '-'}</td>
                  <td>
                    <div className="actions">
                      <button className="action-btn" onClick={() => copyUrl(m.url)}>{I.link}</button>
                      <button className="action-btn action-btn--danger" onClick={() => deleteMedia(m.id)}>{I.trash}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MediaPage;
